import apiClient from './apiClient';
import { buildHipError } from './hipApi';
import { PREFILL_KIND } from '../../constants/hipInvoiceEnums';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * HIP INVOICES API - ABDM M2 HI types, Invoice as a structured form (OR-20..OR-24)
 *
 * Bound to the FROZEN contract in ABDM-M2-HITYPES build-plan.md section 5 INV ("API CONTRACT
 * additions"), not to built backend code. Same rules as hipRecordsApi.js:
 *   1. Every function returns a camelCase domain object - components never see axios envelopes or
 *      snake_case keys. Money is INTEGER PAISE end to end (`ratePaise`, `totalGrossPaise`, ...); no
 *      rupee or float ever crosses this file (typed rupees -> paise is utils/invoiceTotals.js).
 *   2. Errors come from the shared hipApi error builder: err.code (structured `detail.code`),
 *      err.userMessage, err.retryAfter (429 LINK_BLOCKED), err.status, err.field (the wire field
 *      path, e.g. "lines[2].rate_paise"). Extras here: err.lineIndex (0-based line of a per-line
 *      error, else null), err.fieldKey (camelCase of the field: "billNumber", "invoiceType",
 *      "lines", or on a line "description" | "ratePaise" | "discountPaise" | "cgstPaise" | "sgstPaise"),
 *      err.missing (INCOMPLETE_RECORD on link).
 *   3. No URL construction outside this file.
 *   4. Writes send exactly the contract's keys (the server refuses extra fields with 422) and
 *      NEVER totals - the server computes them on every write (OR-24).
 *
 * Error codes (422 unless noted; `field` = the offending input):
 *   save:  BILL_NUMBER_REQUIRED | BILL_NUMBER_TAKEN | TYPE_NOT_ALLOWED | LINES_REQUIRED |
 *          AMOUNT_NOT_INTEGER | AMOUNT_NEGATIVE | DISCOUNT_EXCEEDS_RATE | GST_NOT_ALLOWED |
 *          DESCRIPTION_REQUIRED | TOTAL_NOT_POSITIVE | INVOICE_TOO_LARGE (OR-43; never drops lines)
 *   409:   VERSION_CONFLICT | INVOICE_LOCKED (edit / delete of a LINKED invoice) | SOURCE_ALREADY_USED
 *   404:   SOURCE_NOT_FOUND (prefill)
 *   link:  the whole-request codes of hipRecordsApi.linkRecords (ENCOUNTER_NOT_FOUND |
 *          ABHA_NOT_VERIFIED | RECORD_BEFORE_ABHA_LINK | VISIT_PATIENT_MISMATCH | NOT_DISCHARGED |
 *          INCOMPLETE_RECORD (+err.missing) | LINK_BLOCKED (+err.retryAfter)) | VERSION_CONFLICT |
 *          BUNDLE_NOT_SAVED (500)
 *
 * Base: /api/organisation/hip/invoices
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/hip/invoices';

// ── Error handling ────────────────────────────────────────────────────

const LINE_FIELD = /^lines\[(\d+)\]\.(\w+)$/;
const _camel = (s) => s.replace(/_([a-z])/g, (_m, c) => c.toUpperCase());

/** Shared HIP error + the contract's structured extras (line index, camelCase field key, missing). */
function _invoiceError(error) {
  const err = buildHipError(error);
  const detail = error?.response?.data?.detail;
  if (err.field) {
    const m = LINE_FIELD.exec(err.field);
    err.lineIndex = m ? Number(m[1]) : null;
    err.fieldKey = _camel(m ? m[2] : err.field);
  }
  if (detail && typeof detail === 'object' && Array.isArray(detail.missing)) err.missing = detail.missing;
  return err;
}

// ── Mappers (snake_case contract <-> camelCase) ──────────────────────

function _line(l) {
  return {
    description: l.description,
    ratePaise: l.rate_paise,
    discountPaise: l.discount_paise,
    cgstPaise: l.cgst_paise,
    sgstPaise: l.sgst_paise,
  };
}

/** Exactly the five contract keys of a line - a UI-only key on the caller's line never reaches the wire. */
function _lineBody(l) {
  return {
    description: l.description,
    rate_paise: l.ratePaise,
    discount_paise: l.discountPaise,
    cgst_paise: l.cgstPaise,
    sgst_paise: l.sgstPaise,
  };
}

/** The contract's `Invoice` object. */
function _invoice(i) {
  return {
    invoiceId: i.invoice_id,
    encounterKind: i.encounter_kind,
    encounterId: i.encounter_id,
    billNumber: i.bill_number,
    invoiceType: i.invoice_type,
    lines: (i.lines || []).map(_line),
    totalNetPaise: i.total_net_paise,
    totalGrossPaise: i.total_gross_paise,
    status: i.status,
    state: i.state,
    careContextId: i.care_context_id ?? null,
    prefillSource: { kind: i.prefill_source.kind, ref: i.prefill_source.ref ?? null },
    version: i.version,
    updatedAt: i.updated_at,
    linkedAt: i.linked_at ?? null,
    lastLinkAttemptAt: i.last_link_attempt_at ?? null,
    linkError: i.link_error ?? null,
    retryAfter: i.retry_after ?? null,
  };
}

const _path = (invoiceId) => `${BASE}/${encodeURIComponent(invoiceId)}`;

// ── Prefill ───────────────────────────────────────────────────────────

/**
 * An UNSAVED invoice made from one OPD receipt / the IPD final bill - the drawer's starting point
 * (OR-21). The server reads the bill here and nowhere else (never in the list), so call it once per
 * drawer open. 404 SOURCE_NOT_FOUND | 409 SOURCE_ALREADY_USED (a draft already uses this source).
 * The response does not echo `sourceKind` / `sourceRef`; keep the ones you asked for and send them
 * back as `prefillSource` on create.
 *
 * @param {{ encounterKind: 'OPD'|'IPD', encounterId: string, sourceKind: 'RECEIPT'|'FINAL_BILL',
 *   sourceRef: string }} params
 * @returns {{ billNumber: string, invoiceType: string, lines: Array<{ description, ratePaise,
 *   discountPaise, cgstPaise, sgstPaise }>, totalNetPaise: number, totalGrossPaise: number }}
 */
export async function getInvoicePrefill({ encounterKind, encounterId, sourceKind, sourceRef }) {
  try {
    const { data } = await apiClient.get(`${BASE}/prefill`, {
      params: {
        encounter_kind: encounterKind,
        encounter_id: encounterId,
        source_kind: sourceKind,
        source_ref: sourceRef,
      },
    });
    return {
      billNumber: data.bill_number,
      invoiceType: data.invoice_type,
      lines: (data.lines || []).map(_line),
      totalNetPaise: data.total_net_paise,
      totalGrossPaise: data.total_gross_paise,
    };
  } catch (err) {
    throw _invoiceError(err);
  }
}

// ── CRUD ──────────────────────────────────────────────────────────────

/**
 * Save a new DRAFT. `lines` are integer paise; the server computes the totals. `prefillSource` is
 * the source the lines came from ({ kind: 'RECEIPT'|'FINAL_BILL', ref }) or, for a blank form,
 * { kind: 'NONE', ref: null } (the default).
 *
 * @param {{ encounterKind: 'OPD'|'IPD', encounterId: string, billNumber: string, invoiceType: string,
 *   lines: Array<{ description, ratePaise, discountPaise, cgstPaise, sgstPaise }>,
 *   prefillSource?: { kind: string, ref: string|null } }} params
 * @returns {{ invoice }} 201
 */
export async function createInvoice({
  encounterKind, encounterId, billNumber, invoiceType, lines,
  prefillSource = { kind: PREFILL_KIND.NONE, ref: null },
}) {
  try {
    const { data } = await apiClient.post(BASE, {
      encounter_kind: encounterKind,
      encounter_id: encounterId,
      bill_number: billNumber,
      invoice_type: invoiceType,
      lines: lines.map(_lineBody),
      prefill_source: { kind: prefillSource.kind, ref: prefillSource.ref },
    });
    return { invoice: _invoice(data.invoice) };
  } catch (err) {
    throw _invoiceError(err);
  }
}

/**
 * Replace a DRAFT's bill number / type / lines. `version` is the `version` of the invoice the form
 * was loaded from (optimistic lock): a stale one is 409 VERSION_CONFLICT, a LINKED invoice 409
 * INVOICE_LOCKED.
 *
 * @param {{ invoiceId: string, billNumber: string, invoiceType: string,
 *   lines: Array<{ description, ratePaise, discountPaise, cgstPaise, sgstPaise }>, version: number }} params
 * @returns {{ invoice }}
 */
export async function updateInvoice({ invoiceId, billNumber, invoiceType, lines, version }) {
  try {
    const { data } = await apiClient.put(_path(invoiceId), {
      bill_number: billNumber,
      invoice_type: invoiceType,
      lines: lines.map(_lineBody),
      version,
    });
    return { invoice: _invoice(data.invoice) };
  } catch (err) {
    throw _invoiceError(err);
  }
}

/** Delete a DRAFT (204, nothing returned). A LINKED invoice is 409 INVOICE_LOCKED. */
export async function deleteInvoice({ invoiceId }) {
  try {
    await apiClient.delete(_path(invoiceId));
  } catch (err) {
    throw _invoiceError(err);
  }
}

/**
 * One invoice - a DRAFT, or once LINKED the frozen snapshot of what was sent.
 * @returns {{ invoice }}
 */
export async function getInvoice({ invoiceId }) {
  try {
    const { data } = await apiClient.get(_path(invoiceId));
    return { invoice: _invoice(data.invoice) };
  } catch (err) {
    throw _invoiceError(err);
  }
}

// ── Link ──────────────────────────────────────────────────────────────

/**
 * Link ONE invoice to ABHA (invoices are linked one by one, OR-23; "Link all" on the card never
 * includes them). Linking makes the invoice immutable. `version` = the saved draft's version.
 * Whole-request failures throw - see the codes in the header; a visit with no doctor adds
 * 422 PRACTITIONER_REQUIRED | PRACTITIONER_INVALID | PRACTITIONER_NOT_ALLOWED | NO_DOCTORS_IN_ORGANISATION (OR-59).
 * `practitionerId` = the doctor staff picked for such a visit; sent only when given.
 *
 * @param {{ invoiceId: string, version: number, practitionerId?: string }} params
 * @returns {{ invoice }} status LINKED, `state` LINKING until ABDM answers
 */
export async function linkInvoice({ invoiceId, version, practitionerId }) {
  try {
    const { data } = await apiClient.post(`${_path(invoiceId)}/link`, {
      version,
      ...(practitionerId ? { practitioner_id: practitionerId } : {}),
    });
    return { invoice: _invoice(data.invoice) };
  } catch (err) {
    throw _invoiceError(err);
  }
}
